import React from "react";
import { Link } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { useTranslation } from "../i18n/LanguageContext";

function About() {
  const { t } = useTranslation();

  return (
    <PageLayout
      title={t("about.title", "About PartyBuddy")}
      subtitle={t("about.subtitle", "Connecting people through shared experiences")}
    >
      <div className="space-y-8">
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-blue-300">
            {t("about.storyTitle", "Our Story")}
          </h2>
          <p className="mb-4 text-slate-300">
            {t(
              "about.storyParagraph1",
              "PartyBuddy was born from a simple observation: amazing events happen all the time, but finding the right people to experience them with is harder than it should be."
            )}
          </p>
          <p className="mb-4 text-slate-300">
            {t(
              "about.storyParagraph2",
              "We believe that the best memories come from spontaneous moments and genuine connections. Whether it's a live concert, festival, club night, or cultural event, you shouldn't have to go alone unless you want to."
            )}
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-blue-300">
            {t("about.visionTitle", "Our Vision")}
          </h2>
          <p className="mb-4 text-slate-300">
            {t(
              "about.visionParagraph1",
              "We envision a world where discovering events and finding your perfect party crew is as easy as a few taps. PartyBuddy is building the social layer for events—creating communities around shared interests and unforgettable experiences."
            )}
          </p>
          <p className="text-slate-300">
            {t(
              "about.visionParagraph2",
              "By connecting artists, organizers, venues, and attendees, we're making the event ecosystem more transparent, inclusive, and fun."
            )}
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-blue-300">
            {t("about.todayTitle", "Where We Are Today")}
          </h2>
          <p className="mb-4 text-slate-300">
            {t(
              "about.todayParagraph1",
              "PartyBuddy is still early — we're a small team building and refining the platform in the open. Core features like event discovery, buddy matching, and news & reviews are live today, with more on the way as we listen to early users."
            )}
          </p>
          <p className="text-slate-300">
            {t("about.todayParagraph2Prefix", "That means you might spot rough edges here and there. If you do, we'd genuinely love to hear about it — see our")}{" "}
            <Link to="/contact" className="text-blue-400 transition hover:text-blue-300">
              {t("about.contactLink", "Contact")}
            </Link>{" "}
            {t("about.todayParagraph2Suffix", "page.")}
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-blue-300">
            {t("about.beliefsTitle", "What We Believe In")}
          </h2>
          <ul className="space-y-3 text-slate-300">
            {[
              ["authenticity", "Authenticity:", "Real people, real events, real connections"],
              ["inclusivity", "Inclusivity:", "Events are for everyone. All genres, all vibes, all welcome"],
              ["community", "Community:", "We're building for creators, organizers, and attendees alike"],
              ["experience", "Experience:", "We celebrate the moments that matter—the ones you'll remember forever"],
            ].map(([key, fallbackLabel, fallbackText]) => (
              <li key={key} className="flex gap-3">
                <span className="text-blue-400">→</span>
                <span>
                  <strong>{t(`about.beliefs.${key}.label`, fallbackLabel)}</strong>{" "}
                  {t(`about.beliefs.${key}.text`, fallbackText)}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-blue-300">
            {t("about.closingTitle", "Made with ❤️ for people who love experiences")}
          </h2>
          <p className="text-slate-300">
            {t(
              "about.closingText",
              "Every feature we build, every decision we make, is in service of one goal: helping you find your people and create memories worth having."
            )}
          </p>
        </section>
      </div>
    </PageLayout>
  );
}

export default About;
